import classNames from "classnames";
import * as React from "react";
import KuikaSvg from "../../../ui-kit/kuika-svg/kuika-svg";
import KuikaTooltip from "../../../ui-kit/kuika-tooltip/kuika-tooltip";
import "./kuika-edit-grid.scss";

/**
 * The editable grid of the datasource pages: a framed sheet, a header band, a line between every
 * column and every row, the controls straight in the cells, and — where rows are typed — a
 * trailing draft row instead of an ADD button (`KuikaEditGridFormList`).
 *
 * The anatomy is the table editor's Columns grid (`data-diagram/table-columns-grid.tsx`), shared
 * through one stylesheet mixin (`styles/base/_edit-grid.scss`) rather than copied, so the two
 * cannot drift. Like that grid it is deliberately NOT a `KuikaTable`: every cell is a control,
 * so this is a form laid out in columns, not a data table with sorting and paging. A read-only
 * list (Bluetooth methods, SAP function parameters) draws the same frame with text in its cells.
 *
 * Presentational only: the host owns the rows, the cells and what an edit does.
 */

export interface IKuikaEditGridColumn {
  id: string;
  /** The caption. Empty for the row-action column. */
  header?: React.ReactNode;
  /** The column's grid track — `"minmax(0, 1.2fr)"`, `"52px"`. */
  track: string;
  /** On the header cell. E2E suites assert a table's captions, not its markup. */
  headerTestId?: string;
  /** Lines the caption up with the column's cells: centred on a switch, at the end over a count. */
  align?: "center" | "end";
}

/** The track list travels as a custom property, so the stylesheet keeps one rule for every grid. */
interface IKuikaEditGridStyle extends React.CSSProperties {
  "--kuika-edit-grid-columns": string;
}

/** A nested row's depth, which indents its first cell from the stylesheet. */
interface IKuikaEditGridRowStyle extends React.CSSProperties {
  "--kuika-edit-grid-depth"?: number;
}

export interface IKuikaEditGridProps {
  columns: IKuikaEditGridColumn[];
  /** `KuikaEditGridRow`s — and a `KuikaEditGridEmpty` for a list with nothing in it. */
  children: React.ReactNode;
  /** What the table is, for a screen reader: "Parameters", "Headers". */
  label: string;
  testId?: string;
  className?: string;
}

export const KuikaEditGrid = ({ columns, children, label, testId, className }: IKuikaEditGridProps): JSX.Element => {
  const style: IKuikaEditGridStyle = {
    "--kuika-edit-grid-columns": columns.map((column: IKuikaEditGridColumn): string => column.track).join(" ")
  };

  return (
    <div
      className={classNames("kuika-edit-grid", className)}
      role="table"
      aria-label={label}
      style={style}
      data-testid={testId}
    >
      <div className="kuika-edit-grid__head" role="row">
        {columns.map((column: IKuikaEditGridColumn): JSX.Element => (
          <div
            key={column.id}
            role="columnheader"
            className={classNames("kuika-edit-grid__head-cell", {
              "kuika-edit-grid__cell--center": column.align === "center",
              "kuika-edit-grid__cell--end": column.align === "end"
            })}
            data-testid={column.headerTestId}
          >
            {column.header}
          </div>
        ))}
      </div>
      {children}
    </div>
  );
};

export interface IKuikaEditGridRowProps {
  children: React.ReactNode;
  /**
   * A row whose values the system writes rather than the user types — a header the auth type
   * generates. Its generated cells read as set values (`KuikaEditGridCell generated`).
   */
  locked?: boolean;
  /** The row another panel is showing (a Bluetooth method in the properties panel). */
  selected?: boolean;
  /** A row that opens something. Its cells are text, not controls. */
  onClick?: () => void;
  /** A row under another (a structure's field): its first cell is indented one step per level. */
  depth?: number;
  testId?: string;
  className?: string;
}

export const KuikaEditGridRow = ({
  children,
  locked,
  selected,
  onClick,
  depth,
  testId,
  className
}: IKuikaEditGridRowProps): JSX.Element => {
  const style: IKuikaEditGridRowStyle | undefined = depth ? { "--kuika-edit-grid-depth": depth } : undefined;

  return (
    <div
      role="row"
      className={classNames("kuika-edit-grid__row", className, {
        "kuika-edit-grid__row--locked": locked,
        "kuika-edit-grid__row--selected": selected,
        "kuika-edit-grid__row--clickable": !!onClick,
        "kuika-edit-grid__row--nested": !!depth
      })}
      style={style}
      aria-selected={selected}
      onClick={onClick}
      data-testid={testId}
    >
      {children}
    </div>
  );
};

export interface IKuikaEditGridCellProps {
  children?: React.ReactNode;
  /** The control owns the whole cell — an input, a select. The grid line is its only border. */
  flush?: boolean;
  center?: boolean;
  /** Pushes the content to the cell's far end — a count, a row menu. */
  end?: boolean;
  /** A value the system writes (`KuikaEditGridRow locked`): full strength, in the accent. */
  generated?: boolean;
  testId?: string;
  className?: string;
}

export const KuikaEditGridCell = ({
  children,
  flush,
  center,
  end,
  generated,
  testId,
  className
}: IKuikaEditGridCellProps): JSX.Element => (
  <div
    role="cell"
    className={classNames("kuika-edit-grid__cell", className, {
      "kuika-edit-grid__cell--flush": flush,
      "kuika-edit-grid__cell--center": center,
      "kuika-edit-grid__cell--end": end,
      "kuika-edit-grid__cell--generated": generated
    })}
    data-testid={testId}
  >
    {children}
  </div>
);

export interface IKuikaEditGridRemoveProps {
  /** Names what goes: "Remove parameter X-Trace". */
  label: string;
  onClick: () => void;
  /** The tooltip; the table editor's grid says "Delete column". */
  tooltip?: string;
  testId?: string;
}

/** The row's delete: hover-revealed, as on the Columns grid, and always reachable by Tab. */
export const KuikaEditGridRemove = ({ label, onClick, tooltip, testId }: IKuikaEditGridRemoveProps): JSX.Element => (
  <KuikaTooltip content={tooltip ?? label} position="top">
    <button type="button" className="kuika-edit-grid__remove" aria-label={label} data-testid={testId} onClick={onClick}>
      <KuikaSvg name="trash" width={14} height={14} color="var(--red-5)" />
    </button>
  </KuikaTooltip>
);

export interface IKuikaEditGridEmptyProps {
  children: React.ReactNode;
  /** A list that is the page's subject (Bluetooth methods) gets a block; a side list, one row. */
  tall?: boolean;
  testId?: string;
}

/** A read-only list with nothing in it: one row across every column, saying so. */
export const KuikaEditGridEmpty = ({ children, tall, testId }: IKuikaEditGridEmptyProps): JSX.Element => (
  <div role="row" className="kuika-edit-grid__empty" data-testid={testId}>
    <div
      role="cell"
      className={classNames("kuika-edit-grid__empty-cell", { "kuika-edit-grid__empty-cell--tall": tall })}
    >
      {children}
    </div>
  </div>
);

export interface IKuikaEditGridDetailProps {
  children: React.ReactNode;
  testId?: string;
}

/**
 * What belongs to the row above it but does not fit its columns — a file output's name template
 * and MIME type. One cell across the whole grid, as KuikaTable's expanded row is.
 */
export const KuikaEditGridDetail = ({ children, testId }: IKuikaEditGridDetailProps): JSX.Element => (
  <div role="row" className="kuika-edit-grid__detail" data-testid={testId}>
    <div role="cell" className="kuika-edit-grid__detail-cell">
      {children}
    </div>
  </div>
);

export interface IKuikaEditGridToggleProps {
  expanded: boolean;
  /** Names what opens: "Show the fields of ET_ITEMS". */
  label: string;
  onToggle: () => void;
  testId?: string;
}

/** Opens a row's nested rows. Its own click stops there, so a row that also toggles is not toggled twice. */
export const KuikaEditGridToggle = ({ expanded, label, onToggle, testId }: IKuikaEditGridToggleProps): JSX.Element => (
  <button
    type="button"
    className="kuika-edit-grid__toggle"
    aria-expanded={expanded}
    aria-label={label}
    data-testid={testId}
    onClick={(event: React.MouseEvent<HTMLButtonElement>): void => {
      event.stopPropagation();
      onToggle();
    }}
  >
    <KuikaSvg name={expanded ? "chevron-down" : "chevron-right"} type="arrows" width={14} height={14} />
  </button>
);

/**
 * What a cell's control carries as `data-edit-grid-nav` for keyboard navigation to find it by.
 * `data-testid` is reserved for the E2E-facing ids, which change meaning as a row moves from
 * draft to real, so navigation keys on the row's own stable key instead — the same split Session
 * Variables makes.
 */
export const editGridNavId = (rowKey: number | string, columnId: string): string => `${rowKey}:${columnId}`;

/**
 * Puts the caret in another cell of the grid `from` sits in. A frame later, so a row the edit
 * just appended — the new draft under the one being typed — is on the page first.
 */
export const focusEditGridCell = (from: Element, navId: string): void => {
  const grid: Element | null = from.closest(".kuika-edit-grid");
  if (!grid) return;
  window.requestAnimationFrame((): void => {
    const target: Element | null = grid.querySelector(`[data-edit-grid-nav="${navId}"]`);
    if (target instanceof HTMLElement) {
      target.focus();
    }
  });
};
